import { forwardRef, type ImgHTMLAttributes } from "react";

/**
 * PictureImg — serves AVIF then WebP with a JPG fallback, all with responsive
 * `srcset` widths. Variants must have been generated by
 * `scripts/optimize-images.mjs`, which emits `<basename>-{400,800,1200}.{avif,webp}`
 * next to the source file.
 *
 * Usage:
 *   <PictureImg
 *     src="/blog/hero-foo.jpg"
 *     alt="…"
 *     width={1200}
 *     height={672}
 *     sizes="(min-width: 1024px) 960px, 100vw"
 *     loading="eager"        // for LCP
 *     fetchPriority="high"   // for LCP
 *   />
 *
 * If the source path doesn't match the `<basename>.<ext>` pattern this expects
 * (jpg/jpeg/png), it falls back to a plain <img> — no srcset — so unknown
 * assets keep working.
 */
type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  src: string;
  alt: string;
  /** Optional public-path base for optimized variants when the fallback is a Vite import. */
  optimizedBase?: string;
  /** Widths (px) of the emitted variants. Must match the optimizer script. */
  widths?: number[];
  /** Any `sizes` value valid for <img>. Required for meaningful selection. */
  sizes?: string;
  /** React uses camelCase; forwarded lowercase to the DOM. */
  fetchPriority?: "high" | "low" | "auto";
};

const DEFAULT_WIDTHS = [400, 800, 1200];

const splitPath = (src: string) => {
  const m = src.match(/^(.*)\.(jpg|jpeg|png)$/i);
  if (!m) return null;
  return { base: m[1], ext: m[2].toLowerCase() };
};

const PictureImg = forwardRef<HTMLImageElement, Props>(function PictureImg(
  {
    src,
    alt,
    optimizedBase,
    widths = DEFAULT_WIDTHS,
    sizes,
    fetchPriority,
    className,
    ...rest
  },
  ref,
) {
  const parts = splitPath(src);

  // Forward React's camelCase `fetchPriority` to the lowercase DOM attribute.
  const fp = fetchPriority
    ? ({ fetchpriority: fetchPriority } as Record<string, string>)
    : {};

  if (!parts) {
    return (
      <img
        ref={ref}
        src={src}
        alt={alt}
        sizes={sizes}
        className={className}
        {...fp}
        {...rest}
      />
    );
  }

  const variantBase = optimizedBase ?? parts.base;
  const build = (fmt: "avif" | "webp") =>
    widths.map((w) => `${variantBase}-${w}.${fmt} ${w}w`).join(", ");

  return (
    <picture>
      <source type="image/avif" srcSet={build("avif")} sizes={sizes} />
      <source type="image/webp" srcSet={build("webp")} sizes={sizes} />
      <img
        ref={ref}
        src={src}
        alt={alt}
        sizes={sizes}
        className={className}
        decoding="async"
        {...fp}
        {...rest}
      />
    </picture>
  );
});

export default PictureImg;
